import { useState, useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "react-router-dom";
import { TrendingUp, Calculator, ArrowRight, Sparkles } from "lucide-react";

// Commission benchmarks (industry averages, IT market Feb 2026)
const OTA_FEES = {
  booking: 0.15, // 15% Booking.com standard commission
  airbnb: 0.15,  // 15% Airbnb Host-only fee (IT typical)
};
const HH_FEES = {
  base: 0.08,
  pro: 0.06,
  enterprise: 0.05,
};
const PLAN_MONTHLY = {
  base: 0,
  pro: 29,
  enterprise: 99,
};

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.max(0, n));
const fmtInt = (n) => new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(Math.max(0, n));

export default function RoiCalculator({ IT }) {
  const [rooms, setRooms] = useState(4);
  const [adr, setAdr] = useState(95);
  const [occupancy, setOccupancy] = useState(65); // % occupancy
  const [otaShare, setOtaShare] = useState(70); // % bookings currently via OTA
  const [plan, setPlan] = useState("pro");

  const calc = useMemo(() => {
    const daysMonth = 30;
    const bookedNights = Math.round(rooms * daysMonth * (occupancy / 100));
    const grossMonthly = bookedNights * adr;
    const grossYearly = grossMonthly * 12;

    const otaRevenue = grossYearly * (otaShare / 100);
    const directRevenue = grossYearly * (1 - otaShare / 100);

    // If they keep current mix (OTA + direct free)
    const currentBookingFees = otaRevenue * 0.5 * OTA_FEES.booking; // assume 50% of OTA is Booking
    const currentAirbnbFees = otaRevenue * 0.5 * OTA_FEES.airbnb;
    const currentOtaFees = currentBookingFees + currentAirbnbFees;

    // If they switch: all bookings move to HH direct (best case = 0% OTA)
    const hhFeeRate = HH_FEES[plan];
    const hhFeesAll = grossYearly * hhFeeRate;
    const hhAnnualSub = PLAN_MONTHLY[plan] * 12;
    const totalHhCost = hhFeesAll + hhAnnualSub;

    const savingsAllDirect = currentOtaFees - totalHhCost;

    // Realistic scenario: only shift OTA share to direct (keep same direct part with HH fee too)
    const realHhCost = grossYearly * hhFeeRate + hhAnnualSub;
    const realSavings = currentOtaFees - grossYearly * hhFeeRate - hhAnnualSub;

    const savingsPct = grossYearly > 0 ? (realSavings / grossYearly) * 100 : 0;

    return {
      bookedNights,
      grossMonthly,
      grossYearly,
      currentOtaFees,
      currentBookingFees,
      currentAirbnbFees,
      totalHhCost: realHhCost,
      savings: realSavings,
      savingsPct,
      hhFeeRate,
      hhAnnualSub,
    };
  }, [rooms, adr, occupancy, otaShare, plan]);

  const recommendedPlan = useMemo(() => {
    // Recommend based on gross yearly revenue
    if (calc.grossYearly > 200000) return "enterprise";
    if (calc.grossYearly > 40000) return "pro";
    return "base";
  }, [calc.grossYearly]);

  return (
    <section className="max-w-5xl mx-auto px-6 py-16" data-testid="roi-calculator">
      <div className="mb-8">
        <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-1 flex items-center gap-2">
          <Calculator className="w-3 h-3"/> {IT ? "Calcolatrice ROI" : "ROI Calculator"}
        </div>
        <h2 className="font-display text-4xl">
          {IT ? "Quanto risparmi rispetto a Booking e Airbnb?" : "How much do you save vs Booking & Airbnb?"}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {IT
            ? "Muovi gli slider e scopri il tuo risparmio annuo passando alle prenotazioni dirette con HospitalityHub."
            : "Move the sliders and see how much you save per year by switching to direct bookings with HospitalityHub."}
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Inputs */}
        <div className="lg:col-span-3 border border-border rounded-lg bg-card p-6 space-y-6" data-testid="roi-inputs">
          {/* Rooms */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">{IT ? "Numero di camere" : "Number of rooms"}</Label>
              <div className="font-display text-2xl text-primary" data-testid="roi-rooms-value">{rooms}</div>
            </div>
            <Slider
              value={[rooms]}
              onValueChange={(v) => setRooms(v[0])}
              min={1} max={40} step={1}
              data-testid="roi-rooms-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>1</span><span>40</span></div>
          </div>

          {/* ADR */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">{IT ? "Prezzo medio a notte (ADR)" : "Average nightly rate (ADR)"}</Label>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground text-sm">€</span>
                <Input
                  type="number"
                  value={adr}
                  onChange={(e) => setAdr(Math.max(10, Math.min(1000, Number(e.target.value) || 0)))}
                  className="w-20 h-8 text-right font-display text-lg"
                  min={10} max={1000}
                  data-testid="roi-adr-input"
                />
              </div>
            </div>
            <Slider
              value={[adr]}
              onValueChange={(v) => setAdr(v[0])}
              min={30} max={500} step={5}
              data-testid="roi-adr-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>€30</span><span>€500</span></div>
          </div>

          {/* Occupancy */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">{IT ? "Occupazione media mensile" : "Average monthly occupancy"}</Label>
              <div className="font-display text-2xl text-primary" data-testid="roi-occupancy-value">{occupancy}%</div>
            </div>
            <Slider
              value={[occupancy]}
              onValueChange={(v) => setOccupancy(v[0])}
              min={10} max={100} step={5}
              data-testid="roi-occupancy-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>10%</span><span>100%</span></div>
          </div>

          {/* OTA share */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm">{IT ? "Prenotazioni via Booking + Airbnb" : "Bookings via Booking + Airbnb"}</Label>
              <div className="font-display text-2xl text-primary" data-testid="roi-ota-value">{otaShare}%</div>
            </div>
            <Slider
              value={[otaShare]}
              onValueChange={(v) => setOtaShare(v[0])}
              min={0} max={100} step={5}
              data-testid="roi-ota-slider"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
              <span>{IT ? "Tutte dirette" : "All direct"}</span>
              <span>{IT ? "Tutte OTA" : "All OTA"}</span>
            </div>
          </div>

          {/* Plan toggle */}
          <div>
            <Label className="text-sm mb-2 block">{IT ? "Piano HospitalityHub" : "HospitalityHub plan"}</Label>
            <div className="grid grid-cols-3 gap-2">
              {["base", "pro", "enterprise"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlan(p)}
                  className={`rounded-md border px-3 py-2 text-xs font-semibold uppercase tracking-widest transition ${
                    plan === p
                      ? "border-primary bg-primary text-primary-foreground shadow"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50"
                  }`}
                  data-testid={`roi-plan-${p}`}
                >
                  {p === "base" ? (IT ? "Base" : "Free") : p}
                  <div className="font-display text-sm mt-0.5 normal-case tracking-normal">
                    {(HH_FEES[p] * 100).toFixed(0)}%
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-4" data-testid="roi-results">
          {/* Yearly Gross */}
          <div className="border border-border rounded-lg bg-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              {IT ? "Ricavi lordi annui stimati" : "Estimated yearly gross"}
            </div>
            <div className="font-display text-3xl text-foreground" data-testid="roi-gross-yearly">
              {fmt(calc.grossYearly)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {fmtInt(calc.bookedNights)} {IT ? "notti/mese" : "nights/mo"} × {fmt(adr)}
            </div>
          </div>

          {/* OTA Cost */}
          <div className="border border-border rounded-lg bg-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              {IT ? "Costo attuale Booking + Airbnb" : "Current Booking + Airbnb cost"}
            </div>
            <div className="font-display text-3xl text-destructive" data-testid="roi-current-cost">
              −{fmt(calc.currentOtaFees)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {IT ? "≈ 15% commissioni OTA/anno" : "≈ 15% OTA commissions/year"}
            </div>
          </div>

          {/* HH Cost */}
          <div className="border border-border rounded-lg bg-card p-5">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
              {IT ? "Costo con HospitalityHub" : "Cost with HospitalityHub"}
            </div>
            <div className="font-display text-3xl text-primary" data-testid="roi-hh-cost">
              −{fmt(calc.totalHhCost)}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              {(calc.hhFeeRate * 100).toFixed(0)}% {IT ? "fee" : "fee"}
              {calc.hhAnnualSub > 0 && <> + {fmt(calc.hhAnnualSub)}/{IT ? "anno abb." : "yr sub"}</>}
            </div>
          </div>

          {/* Savings */}
          <div className={`rounded-lg p-5 relative overflow-hidden ${calc.savings > 0 ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
            <div className="text-[10px] uppercase tracking-widest opacity-80 mb-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3"/> {IT ? "Risparmio annuo" : "Yearly savings"}
            </div>
            <div className="font-display text-4xl" data-testid="roi-savings">
              {calc.savings > 0 ? "+" : ""}{fmt(calc.savings)}
            </div>
            <div className="text-[11px] opacity-80 mt-1">
              {calc.savingsPct > 0
                ? (IT ? `+${calc.savingsPct.toFixed(1)}% margine recuperato` : `+${calc.savingsPct.toFixed(1)}% margin recovered`)
                : (IT ? "Aumenta la quota OTA per vedere il risparmio" : "Increase OTA share to see savings")}
            </div>
            {calc.savings > 0 && (
              <Sparkles className="w-16 h-16 absolute -right-2 -bottom-2 opacity-10"/>
            )}
          </div>
        </div>
      </div>

      {/* Recommendation & CTA */}
      <div className="mt-6 border border-primary/30 bg-sand/50 rounded-lg p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between" data-testid="roi-recommendation">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-sage font-semibold mb-1">
            {IT ? "Piano consigliato per te" : "Recommended plan for you"}
          </div>
          <div className="font-display text-2xl">
            {recommendedPlan === "base" && (IT ? "Base — Gratis" : "Free")}
            {recommendedPlan === "pro" && "Pro — €49/mese"}
            {recommendedPlan === "enterprise" && "Enterprise — €199/mese"}
          </div>
          <div className="text-xs text-muted-foreground mt-1 max-w-md">
            {recommendedPlan === "base" && (IT
              ? "Perfetto per iniziare. Nessun costo fisso, solo 8% sulle prenotazioni dirette."
              : "Perfect to start. No fixed cost, only 8% on direct bookings.")}
            {recommendedPlan === "pro" && (IT
              ? "Con il tuo volume, il Pro (6% fee) ripaga l'abbonamento in poche prenotazioni."
              : "At your volume, Pro (6% fee) pays back the subscription in a few bookings.")}
            {recommendedPlan === "enterprise" && (IT
              ? "Enterprise (5% fee) massimizza il margine su volumi elevati e sblocca white-label."
              : "Enterprise (5% fee) maximizes margin at high volume and unlocks white-label.")}
          </div>
        </div>
        <Link to="/auth?mode=register">
          <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 lift" data-testid="roi-cta">
            {IT ? "Inizia gratis" : "Get started free"} <ArrowRight className="w-4 h-4 ml-1"/>
          </Button>
        </Link>
      </div>

      <p className="text-[11px] text-muted-foreground mt-4 text-center italic max-w-3xl mx-auto">
        {IT
          ? "Stime basate su commissioni medie di mercato (Booking.com 15%, Airbnb Host-only 15%). Le fee HospitalityHub sono al netto dei costi Stripe di gestione pagamenti (~1,5% + €0,25 per transazione EU/UK). Risultati reali possono variare in base a stagionalità, mix canali e tariffe applicate."
          : "Estimates based on market average commissions (Booking.com 15%, Airbnb Host-only 15%). HospitalityHub fees exclude Stripe payment processing costs (~1.5% + €0.25 per EU/UK transaction). Actual results may vary based on seasonality, channel mix and applied rates."}
      </p>
    </section>
  );
}
